/* eslint-disable react/prop-types */
import './index.scss';
import ModalBase from '.';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Alert, Button, Input, Select } from '@piggytechng/component-library';
import { PiInfoBold } from 'react-icons/pi';
import { OverideButtonStyle, OverideInputStyle, OverideSelectStyle } from '../../helpers/utils';
import { ModalGenerateStatementProps, StatementPeriod } from '../../types';

const PERIOD_OPTIONS: { value: StatementPeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'last-3-months', label: 'Last 3 months' },
  { value: 'last-6-months', label: 'Last 6 months' },
  { value: 'this-year', label: 'This year' },
  { value: 'custom', label: 'Custom date' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Pin the boundaries to UTC. `dayjs().startOf('day').valueOf()` is local midnight, which in
// WAT (UTC+1) lands on the previous day once read as UTC — so the range spilled a day either side.
const utcStartOfDay = (d: dayjs.Dayjs) => Date.UTC(d.year(), d.month(), d.date(), 0, 0, 0, 0);
const utcEndOfDay = (d: dayjs.Dayjs) => Date.UTC(d.year(), d.month(), d.date(), 23, 59, 59, 999);

// Returns the inclusive range for a preset, as dayjs objects.
const resolveRange = (period: StatementPeriod) => {
  const now = dayjs();

  switch (period) {
    case 'today':
      return { start: now.startOf('day'), end: now.endOf('day') };
    case 'this-month':
      return { start: now.startOf('month'), end: now.endOf('day') };
    case 'last-month':
      return { start: now.subtract(1, 'month').startOf('month'), end: now.subtract(1, 'month').endOf('month') };
    case 'last-3-months':
      return { start: now.subtract(3, 'month').startOf('day'), end: now.endOf('day') };
    case 'last-6-months':
      return { start: now.subtract(6, 'month').startOf('day'), end: now.endOf('day') };
    case 'this-year':
      return { start: now.startOf('year'), end: now.endOf('day') };
    default:
      return null;
  }
};

function ModalGenerateStatement({
  modalIsOpen,
  setIsOpen,
  onSubmit,
  isLoading = false,
  requireUserId = false,
}: ModalGenerateStatementProps) {
  const selectStyle = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const [period, setPeriod] = useState<StatementPeriod>('this-month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [hideAddress, setHideAddress] = useState(false);

  const isCustom = period === 'custom';

  useEffect(() => {
    if (modalIsOpen) {
      setPeriod('this-month');
      setStartDate('');
      setEndDate('');
      setEmail('');
      setUserId('');
      setHideAddress(false);
    }
  }, [modalIsOpen]);

  const closeModal = () => {
    if (isLoading) return;
    setIsOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (requireUserId && !userId.trim()) {
      Alert.error('Please enter the User ID');
      return;
    }

    let range = resolveRange(period);

    if (isCustom) {
      if (!startDate || !endDate) {
        Alert.error('Please select both a start and end date');
        return;
      }
      if (dayjs(endDate).isBefore(dayjs(startDate))) {
        Alert.error('End date cannot be before start date');
        return;
      }
      range = { start: dayjs(startDate).startOf('day'), end: dayjs(endDate).endOf('day') };
    }

    if (!range) {
      Alert.error('Please select a period');
      return;
    }

    if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
      Alert.error('Please enter a valid recipient email');
      return;
    }

    onSubmit({
      start: String(utcStartOfDay(range.start)),
      end: String(utcEndOfDay(range.end)),
      ...(email.trim() ? { providedMail: email.trim() } : {}),
      ...(requireUserId ? { userId: userId.trim() } : {}),
      hideAddress,
    });
  };

  const preview = isCustom ? null : resolveRange(period);

  return (
    <ModalBase modalIsOpen={modalIsOpen} setIsOpen={setIsOpen}>
      <div className="modal-processor modal-processor--sm-title">
        <div className="modal-processor__header">
          <h2>Generate Account Statement</h2>
          <button onClick={closeModal}>&times;</button>
        </div>

        <form className="modal-processor__body" onSubmit={handleSubmit}>
          {requireUserId && (
            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter the customer's User ID"
            />
          )}

          <Select
            {...selectStyle}
            labelStyle={{ color: '#666766' }}
            backgroundColor="#F9FAFB"
            outlineColor="none"
            height="56px"
            label="Period"
            placeholder="Select period"
            options={PERIOD_OPTIONS}
            value={period}
            onChange={(val: StatementPeriod) => setPeriod(val)}
          />

          {isCustom ? (
            <>
              <Input
                {...OverideInputStyle}
                labelStyle={{ color: '#666766' }}
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                {...OverideInputStyle}
                labelStyle={{ color: '#666766' }}
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </>
          ) : (
            preview && (
              <p className="modal-processor__range-preview">
                {preview.start.format('D MMM YYYY')} &ndash; {preview.end.format('D MMM YYYY')}
              </p>
            )
          )}

          <Input
            {...OverideInputStyle}
            labelStyle={{ color: '#666766' }}
            label="Recipient email (optional)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="recipient@email.com"
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={hideAddress} onChange={(e) => setHideAddress(e.target.checked)} />
            <span>Hide Address</span>
          </label>

          <div className="modal-processor__hint">
            <PiInfoBold size={16} />
            <p>
              Statements always go to <strong>office@piggyvest.com</strong>. Add a recipient here to send it to them at
              the same time, so the statement does not have to be forwarded manually afterwards.
            </p>
          </div>

          <div className="modal-processor__actions">
            <button type="button" onClick={closeModal} className="modal-processor__actions-reject">
              Cancel
            </button>
            <Button
              {...OverideButtonStyle}
              className="modal-processor__actions-resolve"
              type="submit"
              loading={isLoading}
            >
              Generate
            </Button>
          </div>
        </form>
      </div>
    </ModalBase>
  );
}

export default ModalGenerateStatement;
